import httpClient from '../services/httpClient';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';

/**
 * Custom hook for managing lottery statistics data
 * Handles fetching, state management, and parameter validation for lottery data
 * Automatically waits for authentication before fetching data
 *
 * @param {string} apiEndpoint - The API endpoint to fetch data from
 * @param {object} defaultParams - Default parameters for the API call
 * @returns {object} - State and handlers for lottery statistics
 */
export const useLotteryStats = (apiEndpoint, defaultParams = {}) => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  // Default values
  const defaultTopN = defaultParams.topN || 5;
  const defaultMinFrequentMatch = defaultParams.minFrequentMatch || 3;
  const defaultMinPreferredMatch = defaultParams.minPreferredMatch || 1;
  // Use useRef to maintain consistent default value across renders
  const defaultPreferredNumRef = useRef(defaultParams.preferredNum || '7,12');
  const defaultPreferredNum = defaultPreferredNumRef.current;

  // State management
  const [combinations, setCombinations] = useState([]);
  const [topNumbers, setTopNumbers] = useState([]);
  const [coldNumbers, setColdNumbers] = useState([]);
  const [lastCombination, setLastCombination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter parameters
  const [topN, setTopN] = useState(defaultTopN);
  const [minFrequentMatch, setMinFrequentMatch] = useState(defaultMinFrequentMatch);
  const [minPreferredMatch, setMinPreferredMatch] = useState(defaultMinPreferredMatch);
  const [preferredNum, setPreferredNum] = useState(defaultPreferredNum);

  // Additional data fields (AI predictions, winners, etc.)
  const [additionalData, setAdditionalData] = useState({
    predictAi: '',
    winner1: '',
    winner2: '',
    winner3: '',
    winner4: '',
    winner5: '',
    winner6: '',
    winner: '',
    allNumbersInCombination: '',
    frequency: '',
    probability: '',
    frequencyAsc: '',
    probabilityAsc: '',
  });

  /**
   * Fetch lottery statistics from API
   */
  const fetchLotteryStats = useCallback(async () => {
    // Don't fetch if not authenticated
    if (authLoading || !isAuthenticated) {
      setLoading(false);
      if (!authLoading && !isAuthenticated) {
        setError('Authentication required. Please log in.');
      }
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Parse preferred numbers
      const preferredNumArray = preferredNum
        .split(',')
        .map(num => num.trim())
        .filter(num => !isNaN(num) && num !== '')
        .map(num => parseInt(num, 10));

      // Build query string
      const queryParams = new URLSearchParams({
        topN: topN,
        minFrequentMatch: minFrequentMatch,
        minPreferredMatch: minPreferredMatch,
        ...(preferredNumArray.length > 0 && { preferredNum: preferredNumArray.join(',') })
      });

      const response = await httpClient.get(`${apiEndpoint}?${queryParams.toString()}`);

      if (!response.ok) {
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      // Update state with fetched data
      setCombinations(data.probabilityCombination || []);
      setTopNumbers(data.topNumbers || []);
      setColdNumbers(data.coldNumbers || []);

      // Handle different API response formats for last combination
      let lastCombo = data.lasCombination || data.lastCombination || data.lastExtraction || null;

      if (lastCombo) {
        // Ensure jolly and superstar are numbers
        if (lastCombo.jolly !== undefined && lastCombo.jolly !== null) {
          lastCombo.jolly = Number(lastCombo.jolly);
        }
        if (lastCombo.superstar !== undefined && lastCombo.superstar !== null) {
          lastCombo.superstar = Number(lastCombo.superstar);
        }

        // If lastExtraction has individual number fields, transform to standard format
        if (!lastCombo.combination && (lastCombo.number1 || lastCombo.number2)) {
          const numbers = [];
          if (lastCombo.number1) numbers.push(Number(lastCombo.number1));
          if (lastCombo.number2) numbers.push(Number(lastCombo.number2));
          if (lastCombo.number3) numbers.push(Number(lastCombo.number3));
          if (lastCombo.number4) numbers.push(Number(lastCombo.number4));
          if (lastCombo.number5) numbers.push(Number(lastCombo.number5));
          if (lastCombo.number6) numbers.push(Number(lastCombo.number6));
          if (lastCombo.jolly) numbers.push(lastCombo.jolly);
          if (lastCombo.superstar) numbers.push(lastCombo.superstar);

          lastCombo = {
            ...lastCombo,
            combination: numbers,
          };
        }

        // Map field names to standard format (handle different API response formats)
        if (!lastCombo.competitionNumber && lastCombo.extractionNumber) {
          lastCombo = {
            ...lastCombo,
            competitionNumber: lastCombo.extractionNumber,
          };
        }
      }

      setLastCombination(lastCombo);

      // Update additional data (handle different field names from API)
      setAdditionalData({
        predictAi: data.predictAi || '',
        winner1: data.winner1 || '',
        winner2: data.winner2 || '',
        winner3: data.winner3 || '',
        winner4: data.winner4 || '',
        winner5: data.winner5 || '',
        winner6: data.winner6 || '',
        winner: data.winner || '',
        allNumbersInCombination: data.allNumbersInCombination || '',
        frequency: data.frequency || '',
        probability: data.probability || '',
        frequencyAsc: data.frequencyAsc || '',
        probabilityAsc: data.probabilityAsc || '',
      });

      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
      console.error('Lottery stats fetch error:', err);
    }
  }, [apiEndpoint, topN, minFrequentMatch, minPreferredMatch, preferredNum, authLoading, isAuthenticated]);

  /**
   * Handle preferred numbers input change
   */
  const handlePreferredNumChange = useCallback((e) => {
    const value = e.target.value;

    // Only allow numbers and commas
    if (/[^0-9,]/.test(value)) {
      return;
    }

    setPreferredNum(value);
  }, []);

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    fetchLotteryStats();
  }, [fetchLotteryStats]);

  /**
   * Handle reset to default values
   */
  const handleReset = useCallback(() => {
    setTopN(defaultTopN);
    setMinFrequentMatch(defaultMinFrequentMatch);
    setMinPreferredMatch(defaultMinPreferredMatch);
    setPreferredNum(defaultPreferredNum);
  }, [defaultTopN, defaultMinFrequentMatch, defaultMinPreferredMatch, defaultPreferredNum]);

  /**
   * Effect hook for initial data loading on component mount
   * Only fetches data once when component loads and authentication is ready
   */
  useEffect(() => {
    // Wait for authentication to be ready before fetching
    if (!authLoading && isAuthenticated) {
      fetchLotteryStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated]);

  /**
   * Effect hook for handling minPreferredMatch changes
   * Only clears preferred numbers when minPreferredMatch is set to 0
   * Does NOT auto-reset when field is empty (allows user to clear it)
   */
  useEffect(() => {
    if (minPreferredMatch === 0) {
      setPreferredNum('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minPreferredMatch]);

  // Return all state and handlers
  return {
    // State
    combinations,
    topNumbers,
    coldNumbers,
    lastCombination,
    loading,
    error,
    additionalData,

    // Parameters
    topN,
    minFrequentMatch,
    minPreferredMatch,
    preferredNum,

    // Handlers
    setTopN,
    setMinFrequentMatch,
    setMinPreferredMatch,
    setPreferredNum,
    handlePreferredNumChange,
    handleSubmit,
    handleReset,
    fetchLotteryStats,
  };
};
